import { useEffect, useRef, useState } from "react"
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import type { DragData } from "./dragData"
import type { DropZone } from "./dropZone"

/**
 * Makes a screen's canvas somewhere to drop: the place a list of items sits
 * on, as opposed to an item in it (useSortableItem) or the page around it
 * (useOffCanvas).
 *
 * It should accept what is placed on it as well as what can be added to it,
 * so letting go of a placed item between or below the others keeps it rather
 * than falling through to the page and taking it off. It lights up (`over`)
 * only for the drags `lightsFor` names, those it will act on; a placed item
 * dropped on it stays where it was.
 */
export function useCanvas<E extends HTMLElement>({
  zone,
  accepts,
  lightsFor = lightsForNothing,
}: {
  zone: DropZone
  /** Which drags may land on the canvas. */
  accepts: (source: DragData) => boolean
  /** Which of those it lights up for while one is over it. */
  lightsFor?: (source: DragData) => boolean
}) {
  const ref = useRef<E>(null)
  const [over, setOver] = useState(false)

  useEffect(
    () =>
      dropTargetForElements({
        element: ref.current!,
        canDrop: ({ source }) => accepts(source.data),
        getData: () => zone.data(),
        onDragEnter: ({ source }) => setOver(lightsFor(source.data)),
        onDragLeave: () => setOver(false),
        onDrop: () => setOver(false),
      }),
    [zone, accepts, lightsFor],
  )

  return { ref, over }
}

const lightsForNothing = () => false
